import { useEffect, useRef, useState } from "react";
import { Save, Download, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const importInput = useRef(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  if (!form) {
    return <div className="animate-pulse h-64 bg-white rounded-xl border border-slate-200" />;
  }

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setForm(data);
      toast.success("Configurações salvas! O site já foi atualizado.");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível salvar."));
    } finally {
      setSaving(false);
    }
  };

  const downloadBackup = async () => {
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hgmoveis-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup baixado! Guarde este arquivo com segurança.");
    } catch (err) {
      toast.error(formatApiError(err, "Falha ao gerar o backup."));
    }
  };

  const importBackup = async (file) => {
    setImporting(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await api.post("/admin/import", payload);
      const total = Object.values(data.imported || {}).reduce((s, n) => s + n, 0);
      toast.success(`Backup restaurado: ${total} registro(s) importados.`);
    } catch (err) {
      toast.error(formatApiError(err, "Arquivo de backup inválido."));
    } finally {
      setImporting(false);
      if (importInput.current) importInput.current.value = "";
    }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="font-heading text-2xl font-bold text-gray-900">Site e Textos</h1>
      <p className="text-sm text-gray-500 mt-1">
        Edite as informações de contato e os textos exibidos na loja — as mudanças aparecem na hora
      </p>

      <form onSubmit={save} className="mt-6 space-y-6 max-w-3xl">
        <section
          className={`rounded-xl p-5 border-2 transition-colors ${
            form.maintenance_mode ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200"
          }`}
          data-testid="maintenance-section"
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <Switch
              checked={!!form.maintenance_mode}
              onCheckedChange={(v) => setForm({ ...form, maintenance_mode: v })}
              className="mt-0.5"
              data-testid="settings-maintenance-mode"
            />
            <span>
              <span className="font-heading font-semibold text-gray-900 flex items-center gap-2">
                Modo Manutenção
                {form.maintenance_mode && (
                  <span className="text-[10px] font-bold uppercase bg-amber-400 text-slate-900 rounded-full px-2 py-0.5" data-testid="maintenance-active-pill">
                    Ativo — salve para aplicar
                  </span>
                )}
              </span>
              <span className="block text-xs text-slate-500 mt-1 leading-relaxed">
                Quando ATIVO: os visitantes veem uma página elegante de "Estamos preparando novidades. Voltaremos em
                breve!" com o botão do WhatsApp, e novas compras/pedidos ficam bloqueados. Você (administrador logado)
                continua navegando e editando a loja normalmente — ideal para preparar produtos, preços e banners antes
                de liberar. Nada é excluído. Ao DESATIVAR e salvar, a loja volta ao normal na hora.
              </span>
            </span>
          </label>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="font-heading font-semibold text-gray-900">Contato e WhatsApp</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Número do WhatsApp (com DDI+DDD, só números)</Label>
              <Input
                value={form.whatsapp_number || ""}
                onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                placeholder="5521982430311"
                className="mt-1.5"
                data-testid="settings-whatsapp-number"
              />
            </div>
            <div>
              <Label>Número exibido no site</Label>
              <Input
                value={form.whatsapp_display || ""}
                onChange={(e) => setForm({ ...form, whatsapp_display: e.target.value })}
                placeholder="(21) 98243-0311"
                className="mt-1.5"
                data-testid="settings-whatsapp-display"
              />
            </div>
          </div>
          <div>
            <Label>Slogan no cabeçalho (abaixo do nome da loja)</Label>
            <Input
              value={form.store_slogan || ""}
              onChange={(e) => setForm({ ...form, store_slogan: e.target.value })}
              className="mt-1.5"
              data-testid="settings-slogan"
            />
          </div>
          <div>
            <Label>Domínio da loja</Label>
            <Input
              value={form.company_domain || ""}
              onChange={(e) => setForm({ ...form, company_domain: e.target.value })}
              placeholder="comprenahg.com.br"
              className="mt-1.5"
              data-testid="settings-domain"
            />
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="font-heading font-semibold text-gray-900">Textos padrão dos produtos</h2>
          <p className="text-xs text-slate-500">Usados em todas as páginas de produto (cada produto pode ter texto próprio).</p>
          <div>
            <Label>Garantia</Label>
            <Textarea value={form.warranty_text || ""} onChange={(e) => setForm({ ...form, warranty_text: e.target.value })} rows={2} className="mt-1.5" data-testid="settings-warranty" />
          </div>
          <div>
            <Label>Montagem</Label>
            <Textarea value={form.assembly_text || ""} onChange={(e) => setForm({ ...form, assembly_text: e.target.value })} rows={2} className="mt-1.5" data-testid="settings-assembly" />
          </div>
          <div>
            <Label>Entrega</Label>
            <Textarea value={form.delivery_text || ""} onChange={(e) => setForm({ ...form, delivery_text: e.target.value })} rows={2} className="mt-1.5" data-testid="settings-delivery" />
          </div>
          <div>
            <Label>Informações importantes (avisos de todos os produtos)</Label>
            <p className="text-xs text-slate-400 mt-0.5">Um aviso por linha. Exibidos automaticamente em todos os produtos, sem precisar copiar em cada cadastro.</p>
            <Textarea value={form.important_info_text || ""} onChange={(e) => setForm({ ...form, important_info_text: e.target.value })} rows={7} className="mt-1.5" data-testid="settings-important-info" />
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="font-heading font-semibold text-gray-900">Integrações e notificações</h2>
          <p className="text-xs text-slate-500">
            Receba cada pedido novo em um sistema externo (ex.: Kommo CRM) via webhook. O pedido fica
            sempre salvo no painel — se a notificação falhar, você reenvia pelo botão no pedido.
            O CPF nunca é enviado completo nas notificações.
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <Switch
              checked={!!form.notifications?.enabled}
              onCheckedChange={(v) => setForm({ ...form, notifications: { ...(form.notifications || {}), enabled: v } })}
              data-testid="settings-notifications-enabled"
            />
            Notificações por webhook ativas
          </label>
          <div>
            <Label>URL do webhook (opcional)</Label>
            <Input
              value={form.notifications?.webhook_url || ""}
              onChange={(e) => setForm({ ...form, notifications: { ...(form.notifications || {}), webhook_url: e.target.value } })}
              placeholder="https://..."
              className="mt-1.5"
              data-testid="settings-webhook-url"
            />
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="font-heading font-semibold text-gray-900">Textos institucionais</h2>
          <div>
            <Label>Texto "Sobre a loja" (rodapé)</Label>
            <Textarea
              value={form.footer_about || ""}
              onChange={(e) => setForm({ ...form, footer_about: e.target.value })}
              rows={3}
              className="mt-1.5"
              data-testid="settings-footer-about"
            />
          </div>
          <div>
            <Label>Região de atendimento (rodapé)</Label>
            <Input
              value={form.footer_location || ""}
              onChange={(e) => setForm({ ...form, footer_location: e.target.value })}
              className="mt-1.5"
              data-testid="settings-footer-location"
            />
          </div>
          <div>
            <Label>Informações de pagamento/entrega (carrinho e checkout)</Label>
            <Textarea
              value={form.payment_note || ""}
              onChange={(e) => setForm({ ...form, payment_note: e.target.value })}
              rows={2}
              className="mt-1.5"
              data-testid="settings-payment-note"
            />
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="font-heading font-semibold text-gray-900">Redes Sociais</h2>
          <p className="text-xs text-slate-500">
            Preencha os endereços completos dos perfis oficiais. Se um campo ficar vazio, o ícone
            correspondente não aparece no site.
          </p>
          <div>
            <Label>Instagram (URL completa)</Label>
            <Input
              value={form.instagram_url || ""}
              onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
              placeholder="https://www.instagram.com/sua_loja/"
              className="mt-1.5"
              data-testid="settings-instagram"
            />
          </div>
          <div>
            <Label>Facebook (URL completa)</Label>
            <Input
              value={form.facebook_url || ""}
              onChange={(e) => setForm({ ...form, facebook_url: e.target.value })}
              placeholder="https://www.facebook.com/sua_loja"
              className="mt-1.5"
              data-testid="settings-facebook"
            />
          </div>
        </section>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving} className="rounded-full px-8" data-testid="settings-save">
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar Alterações"}
          </Button>
          <p className="text-xs text-slate-400">
            Dica: banners, textos e imagens da página inicial são editados no menu Banners.
          </p>
        </div>
      </form>

      {/* backup e migração */}
      <section className="mt-8 max-w-3xl bg-white border border-slate-200 rounded-xl p-5" data-testid="backup-section">
        <h2 className="font-heading font-semibold text-gray-900">Backup e Migração</h2>
        <p className="text-sm text-slate-500 mt-1">
          Baixe um arquivo com <strong>todos os dados da loja</strong> (produtos, categorias, banners,
          regiões de entrega, configurações, avaliações e pedidos). Use para backup ou para migrar a
          loja para outra hospedagem — no servidor novo, restaure pelo botão de importação.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="outline" onClick={downloadBackup} className="rounded-full" data-testid="backup-download">
            <Download className="w-4 h-4" /> Baixar backup completo (JSON)
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            disabled={importing}
            onClick={() => importInput.current?.click()}
            data-testid="backup-import"
          >
            <UploadCloud className="w-4 h-4" /> {importing ? "Importando..." : "Restaurar backup"}
          </Button>
          <input
            ref={importInput}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={(e) => e.target.files[0] && importBackup(e.target.files[0])}
            data-testid="backup-import-input"
          />
        </div>
      </section>
    </div>
  );
}
